import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Webinar, WebinarResource } from "../../types/webinar.types";
import {
  webinarApi,
  toEmbedUrl,
  isEmbedReliable,
  getThumbnailUrl,
} from "../../services/webinar.service";
import { useI18n } from "../../i18n";

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return "";
  return new Date(value).toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

type Tab = "overview" | "resources";

export default function WatchPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, code } = useI18n();
  const [webinar, setWebinar] = useState<Webinar | null>(null);
  const [related, setRelated] = useState<Webinar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  // Files live in a private S3 bucket — fetch a presigned URL, then open it.
  const handleResourceDownload = async (r: WebinarResource) => {
    if (!r.fileKey) {
      // Fallback for older records without a key.
      window.open(r.url, "_blank");
      return;
    }
    try {
      setDownloadingKey(r.fileKey);
      const res = await webinarApi.getResourceDownloadUrl(r.fileKey);
      if (res.data?.downloadUrl) {
        window.open(res.data.downloadUrl, "_blank");
      }
    } catch {
      // Last-resort fallback.
      window.open(r.url, "_blank");
    } finally {
      setDownloadingKey(null);
    }
  };

  // Go back to wherever the user came from (dashboard, webinar list, etc.).
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/webinar");
    }
  };

  useEffect(() => {
    if (!id) return;
    let active = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setTab("overview");

        const [oneRes, allRes] = await Promise.allSettled([
          webinarApi.getWebinarById(id),
          webinarApi.getAllWebinars(),
        ]);

        if (!active) return;

        if (oneRes.status === "fulfilled") {
          setWebinar(oneRes.value.data);
        } else {
          throw new Error(t("webinars.watchPage.loadFailed"));
        }

        if (allRes.status === "fulfilled") {
          setRelated(
            (allRes.value.data || []).filter((w) => w.id !== id).slice(0, 6)
          );
        }
      } catch (err: any) {
        if (active)
          setError(
            t("webinars.watchPage.loadFailed")
          );
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [id]);

  const embedUrl = webinar ? toEmbedUrl(webinar.videoUrl, webinar.source) : "";
  const reliable = webinar ? isEmbedReliable(webinar.source) : false;

  return (
    <div className="flex flex-col flex-1 h-full bg-gray-50">
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shrink-0 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title={t("common.back")}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900 leading-tight truncate">
              {webinar?.title || t("webinars.watchPage.fallbackTitle")}
            </h1>
            {webinar?.createdAt && (
              <p className="text-xs text-gray-500">
                {t("webinars.recordedOn", { date: formatDate(webinar.createdAt, code) })}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto py-6 px-6">
        {loading && (
          <div className="w-full">
            <div className="aspect-video bg-gray-200 rounded-2xl animate-pulse" />
          </div>
        )}
        {error && (
          <div className="w-full">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && webinar && (
          <div className="w-full flex flex-col lg:flex-row gap-8">
            {/* Left column */}
            <div className="flex-1 space-y-8 min-w-0">
              {/* Video player */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="relative aspect-video bg-gray-900 group">
                  <iframe
                    src={embedUrl}
                    title={webinar.title}
                    className="absolute inset-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                  {/* Cosmetic top brand bar — pointer-events-none so the iframe stays clickable */}
                  <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-b from-black/40 to-transparent">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center border border-white/20 text-white font-bold text-xs">
                        L
                      </div>
                      <span className="text-white font-medium text-sm drop-shadow-md">
                        {t("webinars.watchPage.brand")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fallback for sources that often block embedding (e.g. Zoom) */}
              {!reliable && (
                <div className="-mt-4 text-sm text-gray-500 flex items-center gap-2">
                  <i className="fa-solid fa-circle-info"></i>
                  {t("webinars.watchPage.playbackFallback")}
                  <a
                    href={webinar.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-600 font-medium hover:underline"
                  >
                    {t("webinars.watchPage.openInNewTab")}
                  </a>
                  .
                </div>
              )}

              {/* Tabs */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex gap-6">
                    <button
                      onClick={() => setTab("overview")}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm cursor-pointer transition-colors ${
                        tab === "overview"
                          ? "border-brand-500 text-brand-600 font-semibold"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium"
                      }`}
                    >
                      {t("webinars.watchPage.tabs.overview")}
                    </button>
                    <button
                      onClick={() => setTab("resources")}
                      className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm cursor-pointer transition-colors ${
                        tab === "resources"
                          ? "border-brand-500 text-brand-600 font-semibold"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 font-medium"
                      }`}
                    >
                      {t("webinars.watchPage.tabs.resources")}
                    </button>
                  </nav>
                </div>

                {/* Overview */}
                {tab === "overview" && (
                  <div className="max-w-none text-gray-600">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {t("webinars.watchPage.aboutTitle")}
                    </h3>
                    {webinar.description ? (
                      <p className="mb-4 leading-relaxed whitespace-pre-line">
                        {webinar.description}
                      </p>
                    ) : (
                      <p className="mb-4 text-gray-400">
                        {t("webinars.watchPage.noDescription")}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-6">
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium capitalize">
                        {webinar.source}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium capitalize">
                        {webinar.status}
                      </span>
                    </div>
                  </div>
                )}

                {/* Resources */}
                {tab === "resources" &&
                  (webinar.resources && webinar.resources.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {webinar.resources.map((r, idx) => {
                        const isFile = r.type === "file";
                        const inner = (
                          <>
                            <div
                              className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${
                                isFile
                                  ? "bg-red-50 text-red-500"
                                  : "bg-blue-50 text-blue-500"
                              }`}
                            >
                              <i
                                className={`fa-solid ${
                                  isFile ? "fa-file-arrow-down" : "fa-link"
                                } text-xl`}
                              ></i>
                            </div>
                            <div className="ml-4 flex-1 min-w-0 text-left">
                              <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-brand-700">
                                {r.title}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {isFile
                                  ? `${r.fileType || t("webinars.watchPage.file")}${
                                      r.fileSize ? ` • ${r.fileSize}` : ""
                                    }`
                                  : t("webinars.watchPage.externalLink")}
                              </p>
                            </div>
                            <i
                              className={`fa-solid ${
                                isFile
                                  ? downloadingKey === r.fileKey
                                    ? "fa-spinner fa-spin"
                                    : "fa-download"
                                  : "fa-arrow-up-right-from-square text-sm"
                              } text-gray-400 group-hover:text-brand-500 ml-2`}
                            ></i>
                          </>
                        );

                        const className =
                          "flex items-center p-4 rounded-xl border border-gray-100 hover:border-brand-200 hover:bg-brand-50 transition-all group w-full";

                        // Files: fetch a presigned URL on click (private S3 bucket).
                        if (isFile) {
                          return (
                            <button
                              key={idx}
                              onClick={() => handleResourceDownload(r)}
                              disabled={downloadingKey === r.fileKey}
                              className={className}
                            >
                              {inner}
                            </button>
                          );
                        }
                        // Links: open directly.
                        return (
                          <a
                            key={idx}
                            href={r.url}
                            target="_blank"
                            rel="noreferrer"
                            className={className}
                          >
                            {inner}
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <i className="fa-regular fa-folder-open text-3xl text-gray-300"></i>
                      <p className="mt-3 text-sm text-gray-500">
                        {t("webinars.watchPage.noResources")}
                      </p>
                    </div>
                  ))}
              </div>
            </div>

            {/* Right column — Related Videos */}
            <div className="w-full lg:w-80 space-y-6 shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">
                  {t("webinars.watchPage.relatedVideos")}
                </h3>

                {related.length === 0 ? (
                  <p className="text-sm text-gray-400">{t("webinars.watchPage.noOtherWebinars")}</p>
                ) : (
                  <div className="space-y-4">
                    {related.map((r) => {
                      const thumb = getThumbnailUrl(r.videoUrl, r.source);
                      return (
                        <button
                          key={r.id}
                          onClick={() => navigate(`/webinar/${r.id}`)}
                          className="flex gap-3 group w-full text-left"
                        >
                          <div className="w-28 h-16 rounded-lg bg-gray-200 shrink-0 overflow-hidden relative">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={r.title}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                                <i className="fa-solid fa-video"></i>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                              <div className="w-7 h-7 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <i className="fa-solid fa-play text-gray-800 text-xs ml-0.5"></i>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col justify-center min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-brand-600 transition-colors">
                              {r.title}
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              <i className="fa-regular fa-calendar mr-1"></i>
                              {new Date(r.createdAt).toLocaleDateString(code, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {related.length > 0 && (
                  <button
                    onClick={() => navigate("/webinar")}
                    className="w-full mt-5 py-2.5 text-sm font-semibold text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors border border-brand-100"
                  >
                    {t("webinars.watchPage.viewAllVideos")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
